<script setup lang="ts">
import debounce from 'lodash/debounce.js'
import type { Video } from '@/models/content'
import { getStringOrFirstElement } from '@/utils/array'
import { formatVideoDuration } from '@/utils/time'

import { useGoogleAnalyticsPageView } from '~/composables/google-analytics'
import IconReplay from 'assets/icons/replay.svg'
import IconPlay from 'assets/icons/play.svg'
import { useMembershipStore } from '~/stores/membership'
import { useAuthStore } from '~/stores/auth'

const { $cloudinary, $graphqlClient, $modal, $restClient, $seoManager } =
  useNuxtApp()
const authStore = useAuthStore()
const membershipStore = useMembershipStore()
const isMobile = useMobile()
const route = useRoute()

const loading = ref(true)
const video = ref<Video | null>(null)
const relatedVideos = ref<Video[]>([])
const sharedVideos = ref<Video[]>([])
const formattedDuration = ref('')
const isVideoAvailable = ref(false)
const isPreview = ref(false)
const videoID = ref(0)
const watchVideo = ref(false)
const endedVideo = ref(false)
const isPublicVideo = ref(false)

const isAuthenticated = computed(() => authStore.isAuthenticated)
const isAuthorized = computed(
  () =>
    membershipStore.isSubscriberActive ||
    membershipStore.hasOnlyTrialActive ||
    isPublicVideo.value,
)

const slug = getStringOrFirstElement(route.params.slug) as unknown as string

const fetchVideo = async () => {
  loading.value = true
  video.value = await $graphqlClient.getVideoDetail({ slug })

  if (!video.value) {
    loading.value = false
    return showError({ statusCode: 404, statusMessage: 'Video not found' })
  }

  const membershipStatuses = await $restClient.getMembershipStatuses({
    slugs: ['subscriber', 'trial', 'special-issues'],
  })
  membershipStore.setMembershipStatuses(membershipStatuses)

  relatedVideos.value = video.value.relatedVideos.filter(
    (_video) => _video.id !== video.value.id,
  )
  sharedVideos.value = relatedVideos.value?.slice(0, 3)
  formattedDuration.value = formatVideoDuration(video.value.duration)

  isPublicVideo.value = video.value.isPublic

  if (isAuthorized.value) {
    videoID.value = video.value.vimeoId

    isPreview.value = false
    isVideoAvailable.value = true
    watchVideo.value = true
    endedVideo.value = false
    loading.value = false

    return
  }

  if (video.value.previewVimeoId) {
    videoID.value = video.value.previewVimeoId

    isPreview.value = true
    isVideoAvailable.value = true
    loading.value = false
    return
  }

  isPreview.value = false
  isVideoAvailable.value = false
  loading.value = false
}

const handleWatchNow = () => {
  watchVideo.value = true
}

const handleEndedVideo = (value: boolean) => {
  endedVideo.value = value
}

const handleButton = () => {
  if (!isAuthenticated.value && !isPreview.value) {
    $modal.open('login')
  } else if (isPreview.value || isAuthorized.value) {
    watchVideo.value = true
  } else {
    $modal.open('requestATrial')
  }
}

const videoDescriptionKey = ref(0)
const videoDescription = ref<HTMLParagraphElement | null>(null)
const descriptionLineHeight = 22
const showCollapsed = ref(false)
const collapsed = ref(false)
const lineClamp = ref('')

const getLineClamp = () => {
  const lineNumber =
    videoDescription.value?.scrollHeight / descriptionLineHeight
  videoDescriptionKey.value += 1

  if (window.innerWidth < 768 && lineNumber && lineNumber > 2) {
    showCollapsed.value = true
    return 'line-clamp-2'
  }

  if (lineNumber && lineNumber > 3) {
    showCollapsed.value = true
    return 'line-clamp-3'
  }

  showCollapsed.value = false
  return ''
}

const getLineClampDebounced = debounce(() => {
  lineClamp.value = getLineClamp()
}, 500)

const { data } = useAsyncData(`seo-data-video-${slug}`, async () => {
  const { video: seoInformation } =
    await $graphqlClient.getVideoSeoInformation(slug)

  const image = {
    twitterImage: {
      mediaItemUrl: seoInformation?.videos?.thumbnail?.node?.mediaItemUrl,
    },

    opengraphImage: {
      mediaItemUrl: seoInformation?.videos?.thumbnail?.node?.mediaItemUrl,
    },
  }

  return $seoManager.generateSeoInfo(
    {
      title: seoInformation?.title,
    },
    image,
  )
})

const seo = computed(() => data.value)

onAuthenticated(fetchVideo)
onMounted(async () => {
  await fetchVideo()

  if (!window) return

  lineClamp.value = getLineClamp()
  window.addEventListener('resize', getLineClampDebounced)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', getLineClampDebounced)
})

useHead(seo)
useGoogleAnalyticsPageView(seo.value?.title)
</script>

<template>
  <div class="flex grow flex-col justify-center">
    <ClientOnly>
      <LoadingSpinner
        v-if="loading || authStore.token === undefined"
        wrap-screen
      />

      <section
        v-else
        class="grow pb-20"
        :class="{ 'lg:pb-36': relatedVideos.length }"
        data-testid="video-player"
      >
        <section class="overflow-hidden bg-s-800 lg:bg-s-900">
          <div
            v-if="isVideoAvailable && watchVideo"
            class="responsive-height-aspect-ratio-16-9"
          >
            <div v-if="!endedVideo">
              <div
                class="absolute h-full w-full"
                data-testid="video-player-wrapper"
              >
                <VPlayer
                  :video-id="videoID"
                  autoplay
                  :title="video?.title"
                  :is-preview="isPreview"
                  :is-free="isPublicVideo"
                  :categories="video?.categories"
                  @ended-video="handleEndedVideo(true)"
                />
              </div>
            </div>
            <div
              v-else
              class="mx-auto flex h-full min-h-[206px] w-full max-w-video-container flex-col items-center justify-center gap-3 text-white sm:min-h-[255px] md:min-h-[428px] lg:min-h-[572px] header-breakpoint:min-h-[698px]"
              data-testid="video-player-wrapper"
            >
              <div
                v-if="sharedVideos.length > 0 && isAuthorized"
                class="mb-4 hidden w-full gap-5 px-5 text-s-900 md:flex header-breakpoint:px-0"
              >
                <VerticalCard
                  v-for="_video in sharedVideos"
                  :key="_video.id"
                  :slug="_video.slug"
                  content-type="video"
                  :published-date="_video.publishedDate"
                  :duration="_video.duration"
                  :title="_video.title"
                  :excerpt="_video.excerpt"
                  :pills="[]"
                  :cover-url="_video.coverUrl"
                  class="shrink-0 md:flex-1"
                  data-testid="video-card"
                  custom-container-classes="min-h-[250px] lg:min-h-[320px] xl:min-h-[350px]"
                />
              </div>
              <span v-if="!isAuthorized" class="text-base font-semibold">
                To watch the full video
              </span>

              <button
                v-if="
                  !isAuthorized ||
                  (!membershipStore.isSubscriberActive && isPublicVideo)
                "
                class="btn primary-btn flex min-w-[190px] max-w-[190px] items-center justify-center gap-x-2 rounded px-4 py-2 text-sm font-semibold text-s-900"
                @click="$modal.open('requestATrial')"
              >
                Request a Trial
              </button>

              <div v-if="!isAuthenticated" class="flex gap-1 text-xs">
                <span>Already a Subscriber?</span>
                <span
                  class="cursor-pointer font-bold text-p-500"
                  @click="$modal.open('login')"
                >
                  Sign In
                </span>
              </div>

              <div class="mt-1 flex flex-col items-center justify-center">
                <span class="text-xs font-bold">Replay</span>
                <IconReplay
                  class="h-16 w-16 cursor-pointer"
                  @click="handleEndedVideo(false)"
                />
              </div>
            </div>
          </div>
          <div
            v-else
            data-testid="video-player-wrapper"
            class="relative min-h-[206px] sm:min-h-[255px] md:min-h-[428px] lg:min-h-[572px] header-breakpoint:min-h-[698px]"
          >
            <!-- Video Cover -->
            <div
              class="relative left-0 top-0 h-full w-full bg-s-400 pb-9/16 md:absolute md:pb-0"
            >
              <img
                width="1000"
                height="600"
                :src="$cloudinary.resize(video?.coverUrl, 'video-cover')"
                alt=""
                class="absolute left-0 top-0 h-full w-full object-cover object-top md:hidden"
              />
              <img
                width="1000"
                height="600"
                :src="video?.coverUrl"
                alt=""
                class="absolute left-0 top-0 hidden h-full w-full object-cover object-top md:block"
              />

              <!-- Overlay -->
              <div
                class="absolute left-0 top-0 h-full w-full items-center justify-center bg-black bg-opacity-50"
              />

              <!-- Play Icon -->
              <button
                v-if="isMobile"
                class="absolute left-0 top-0 flex h-full w-full items-center justify-center"
                @click="handleButton"
              >
                <IconPlay class="w-14 text-white/80" />
              </button>
            </div>

            <!-- Video Info -->
            <div
              class="relative hidden w-full md:left-0 md:top-0 md:block md:py-24 md:text-white"
            >
              <DetailVideoCard
                class="mx-auto max-w-content-container px-5"
                :slug="video?.slug"
                :published-date="video?.publishedDate"
                :duration="video?.duration"
                :title="video?.title"
                :excerpt="video?.excerpt"
                :pills="video?.tags"
                :is-preview="isPreview"
                :has-authenticated="isAuthenticated"
                :has-subscription="isAuthorized"
                @play-video="handleWatchNow"
              />
            </div>
          </div>
        </section>

        <div class="bg-[#efefef]">
          <div class="mx-auto max-w-content-container px-5 py-7">
            <p class="mb-3 text-xs font-semibold leading-[17px] text-s-900">
              {{ video?.publishedDate }} | {{ formattedDuration }}
            </p>
            <h1
              data-testid="title"
              class="mb-2.5 text-xl font-bold leading-[26px]"
            >
              {{ video?.title }}
            </h1>

            <hr class="mb-3 hidden h-0.5 w-16 bg-p-500 md:block" />

            <div class="mb-3 flex gap-2">
              <LinkedPill
                v-for="tag in video?.tags || []"
                :key="tag.id"
                variant="white"
                :slug="tag.slug"
              >
                <p class="text-xxs leading-[14px]">
                  {{ tag.name }}
                </p>
              </LinkedPill>
            </div>

            <div class="mb-4">
              <p
                class="mb-1 line-clamp-2 text-base font-semibold leading-[22px]"
              >
                {{ video?.excerpt }}
              </p>
              <div v-if="video?.description" class="relative">
                <p
                  ref="videoDescription"
                  :key="videoDescriptionKey"
                  class="text-sm lg:text-base lg:leading-[22px]"
                  :class="collapsed ? '' : lineClamp"
                  :style="{ lineHeight: descriptionLineHeight + 'px' }"
                >
                  {{ video?.description }}
                </p>
                <button
                  v-if="showCollapsed"
                  class="bottom-0 right-0 bg-[#efefef] font-bold"
                  :class="collapsed ? 'relative mt-3' : 'absolute'"
                  @click="collapsed = !collapsed"
                >
                  <div
                    class="absolute -left-16 h-full w-16 bg-gradient-to-r from-transparent to-[#efefef]"
                  ></div>
                  {{ collapsed ? 'Show less' : '...more' }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="relatedVideos.length">
          <VideoList title="Related videos" :videos="relatedVideos" />
        </div>
      </section>
    </ClientOnly>
  </div>
</template>

<style scoped lang="scss">
$max-header-height: 164px;
$video-container-max-height: calc(100vh - #{$max-header-height});
// 1.7777777778 is the result of 16 / 9 = 1.777777777...
// and is used to calculate the width when multiplied by the height.
$video-container-max-width: calc(#{$video-container-max-height} * 1.7777777778);
.responsive-height-aspect-ratio-16-9 {
  position: relative;
  margin: 0 auto;
  aspect-ratio: 16/9;
  width: $video-container-max-width;
  max-width: 100vw;
}
</style>
